import React, { useContext, useEffect, useState } from 'react';
import AppContext from '../../../../App/AppContext';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { TextBox } from '../../../../Core/FormInput';
import { Grid, InputLabel, Typography, CircularProgress } from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import EditIcon from '@material-ui/icons/Edit';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { getMABudgets_GetByAccountCode, postMABudgets_AddUpdate } from '../../../../Core/Service/ManageAccount/NominalBudget-Modal-Service';

const StyledSubTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: '#6b6b6b',
        color: 'white',
        borderBottom: '1px solid rgba(0,0,0,0.3)',
        borderRight: '2px solid #6b6b6b',
        borderLeft: '2px solid #6b6b6b',
        border: 'none',
        padding: 5,
        fontSize: 13,
        fontWeight: 600
    },
    body: {
        fontSize: 13,
        backgroundColor: 'white',
        borderBottom: '1px solid rgba(0,0,0,0.3)',
        padding: 5
    }
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {}
}))(TableRow);

const useStyles = makeStyles({
    table: {
        minWidth: '100%'
    },
    loaderContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
    },
    compactInput: {
        '& input': {
            padding: '4px 5px !important',
            fontSize: '0.8rem',
            height: '30px',
            boxSizing: 'border-box',
            '-moz-appearance': 'textfield',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0
        }
    }
}
});

function NominalBudget({ selectedRowData }) {
    const classes = useStyles();
    const [budgetItems, setBudgetItems] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newBudget, setNewBudget] = useState({
        maBudgetYear: '',
        maBudgetJan: null,
        maBudgetFeb: null,
        maBudgetMar: null,
        maBudgetApr: null,
        maBudgetMay: null,
        maBudgetJun: null,
        maBudgetJul: null,
        maBudgetAug: null,
        maBudgetSep: null,
        maBudgetOct: null,
        maBudgetNov: null,
        maBudgetDec: null
    });
    const { showToast } = useContext(AppContext);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchBudgetData();
    }, [selectedRowData]);

    const fetchBudgetData = async () => {
        setLoading(true);
        try {
            const res = await getMABudgets_GetByAccountCode(selectedRowData.accountCode);
            if (res.success) {
                setBudgetItems(res?.data?.list || []);
            }
        } catch (error) {
            showToast(`Error loading budget data: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleBudgetChange = (index, field, value) => {
        const updatedItems = [...budgetItems];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value !== '' ? parseFloat(value) : null
        };
        setBudgetItems(updatedItems);
    };

    const handleSave = async () => {
        try {
            if (isAddingNew) {
                if (!/^\d{4}$/.test(newBudget.maBudgetYear)) {
                    showToast('Please enter a valid 4-digit year');
                    return;
                }

                if (budgetItems.some((item) => item.maBudgetYear === parseInt(newBudget.maBudgetYear))) {
                    showToast('Budget for this year already exists');
                    return;
                }

                const payload = {
                    MABudgetItems: [
                        {
                            ...newBudget,
                            maBudgetYear: parseInt(newBudget.maBudgetYear),
                            maBudgetAccountCode: selectedRowData.accountCode
                        }
                    ]
                };

                const res = await postMABudgets_AddUpdate(payload);
                if (res.success) {
                    setBudgetItems((prev) => [...prev, payload.MABudgetItems[0]]);
                    setIsAddingNew(false);
                    setNewBudget({
                        maBudgetYear: '',
                        maBudgetJan: null,
                        maBudgetFeb: null,
                        maBudgetMar: null,
                        maBudgetApr: null,
                        maBudgetMay: null,
                        maBudgetJun: null,
                        maBudgetJul: null,
                        maBudgetAug: null,
                        maBudgetSep: null,
                        maBudgetOct: null,
                        maBudgetNov: null,
                        maBudgetDec: null
                    });
                    showToast('Budget added successfully');
                } else {
                    showToast(res.message || 'Add failed');
                }
            } else if (selectedIndex !== null) {
                const editedItem = budgetItems[selectedIndex];
                const payload = {
                    MABudgetItems: [
                        {
                            ...editedItem,
                            maBudgetAccountCode: selectedRowData.accountCode
                        }
                    ]
                };

                const res = await postMABudgets_AddUpdate(payload);
                if (res.success) {
                    showToast('Budget updated successfully');
                    setSelectedIndex(null);
                    fetchBudgetData();
                } else {
                    showToast(res.message || 'Update failed');
                }
            }
        } catch (error) {
            showToast(`Error: ${error.message}`);
        }
    };

    const copyJanToAll = (index) => {
        const janValue = budgetItems[index].maBudgetJan;
        const updatedItems = budgetItems.map((item, i) => {
            if (i === index) {
                return {
                    ...item,
                    maBudgetFeb: janValue,
                    maBudgetMar: janValue,
                    maBudgetApr: janValue,
                    maBudgetMay: janValue,
                    maBudgetJun: janValue,
                    maBudgetJul: janValue,
                    maBudgetAug: janValue,
                    maBudgetSep: janValue,
                    maBudgetOct: janValue,
                    maBudgetNov: janValue,
                    maBudgetDec: janValue
                };
            }
            return item;
        });
        setBudgetItems(updatedItems);
    };

    const calculateTotal = (item) => {
        const months = [
            'maBudgetJan',
            'maBudgetFeb',
            'maBudgetMar',
            'maBudgetApr',
            'maBudgetMay',
            'maBudgetJun',
            'maBudgetJul',
            'maBudgetAug',
            'maBudgetSep',
            'maBudgetOct',
            'maBudgetNov',
            'maBudgetDec'
        ];
        return months.reduce((sum, month) => sum + (parseFloat(item[month]) || 0), 0);
    };

    const addNewBudget = () => {
        setIsAddingNew(true);
    };

    return (
        <Grid container direction="row" spacing={2} justifyContent="center" alignItems="center">
            <Grid item xs={6}>
                <Grid container>
                    <Grid item xs={12}>
                        <InputLabel shrink>Account Code</InputLabel>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">{selectedRowData.accountCode}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={6}>
                <Grid container>
                    <Grid item xs={12}>
                        <InputLabel shrink>Description</InputLabel>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">{selectedRowData.en}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <TableContainer style={{ height: 317, border: '1px solid rgba(0,0,0,0.2)' }}>
                    <Table className={classes.table} size="small">
                        <TableHead>
                            <TableRow>
                                <StyledSubTableCell width="3%">
                                    <AddBoxIcon onClick={addNewBudget} style={{ color: 'White', verticalAlign: 'middle', cursor: 'pointer' }} />
                                </StyledSubTableCell>
                                {/* <StyledSubTableCell width="5%">Year</StyledSubTableCell> */}
                                <StyledSubTableCell width={selectedIndex !== null || isAddingNew ? '5%' : '3%'}>Year</StyledSubTableCell>
                                <StyledSubTableCell width={selectedIndex !== null || isAddingNew ? '9%' : '7%'}>Jan</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Feb</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Mar</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Apr</StyledSubTableCell>
                                <StyledSubTableCell width="7%">May</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Jun</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Jul</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Aug</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Sep</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Oct</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Nov</StyledSubTableCell>
                                <StyledSubTableCell width="8%">Dec</StyledSubTableCell>
                                <StyledSubTableCell width="7%">Total</StyledSubTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={15}>
                                        <div className={classes.loaderContainer}>
                                            <CircularProgress />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {isAddingNew && (
                                        <StyledTableRow>
                                            <StyledSubTableCell>
                                                <CheckCircleIcon onClick={handleSave} style={{ cursor: 'pointer', color: 'green' }} />
                                            </StyledSubTableCell>
                                            <StyledSubTableCell>
                                                <TextBox
                                                    value={newBudget.maBudgetYear}
                                                    className={classes.compactInput}
                                                    onChange={(e) =>
                                                        setNewBudget((prev) => ({
                                                            ...prev,
                                                            maBudgetYear: e.target.value.replace(/\D/g, '').slice(0, 4)
                                                        }))
                                                    }
                                                    inputProps={{ maxLength: 4 }}
                                                />
                                            </StyledSubTableCell>
                                            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month) => (
                                                <StyledSubTableCell key={month}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <TextBox
                                                            type="number"
                                                            className={classes.compactInput}
                                                            value={newBudget[`maBudget${month}`] || ''}
                                                            onChange={(e) =>
                                                                setNewBudget((prev) => ({
                                                                    ...prev,
                                                                    [`maBudget${month}`]: e.target.value
                                                                }))
                                                            }
                                                        />
                                                        {month === 'Jan' && (
                                                            <DoubleArrowIcon
                                                                fontSize="small"
                                                                color="secondary"
                                                                style={{ marginLeft: 4, cursor: 'pointer', fontSize: '14px' }}
                                                                onClick={() => {
                                                                    const janValue = newBudget.maBudgetJan;
                                                                    setNewBudget((prev) => ({
                                                                        ...prev,
                                                                        maBudgetFeb: janValue,
                                                                        maBudgetMar: janValue,
                                                                        maBudgetApr: janValue,
                                                                        maBudgetMay: janValue,
                                                                        maBudgetJun: janValue,
                                                                        maBudgetJul: janValue,
                                                                        maBudgetAug: janValue,
                                                                        maBudgetSep: janValue,
                                                                        maBudgetOct: janValue,
                                                                        maBudgetNov: janValue,
                                                                        maBudgetDec: janValue
                                                                    }));
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </StyledSubTableCell>
                                            ))}
                                            <StyledSubTableCell>{calculateTotal(newBudget)?.toLocaleString()}</StyledSubTableCell>
                                        </StyledTableRow>
                                    )}
                                    {budgetItems.map((item, index) => (
                                        <StyledTableRow key={item.maBudgetID}>
                                            <StyledSubTableCell>
                                                {selectedIndex === index ? (
                                                    <CheckCircleIcon onClick={handleSave} style={{ cursor: 'pointer', color: 'green' }} />
                                                ) : (
                                                    <EditIcon onClick={() => setSelectedIndex(index)} style={{ cursor: 'pointer', color: '#2964D9' }} />
                                                )}
                                            </StyledSubTableCell>
                                            <StyledSubTableCell>
                                                {selectedIndex === index ? <Typography>{item.maBudgetYear}</Typography> : item.maBudgetYear}
                                            </StyledSubTableCell>
                                            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month) => (
                                                <StyledSubTableCell key={month}>
                                                    {selectedIndex === index ? (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <TextBox
                                                                type="number"
                                                                className={classes.compactInput}
                                                                value={item[`maBudget${month}`] || ''}
                                                                onChange={(e) => handleBudgetChange(index, `maBudget${month}`, e.target.value)}
                                                            />
                                                            {month === 'Jan' && (
                                                                <DoubleArrowIcon
                                                                    fontSize="small"
                                                                    color="secondary"
                                                                    style={{ marginLeft: 8, cursor: 'pointer' }}
                                                                    onClick={() => copyJanToAll(index)}
                                                                />
                                                            )}
                                                        </div>
                                                    ) : (
                                                        item[`maBudget${month}`]?.toLocaleString() || '-'
                                                    )}
                                                </StyledSubTableCell>
                                            ))}
                                            <StyledSubTableCell>{calculateTotal(item)?.toLocaleString()}</StyledSubTableCell>
                                        </StyledTableRow>
                                    ))}
                                    {budgetItems.length === 0 && !isAddingNew && !loading && (
                                        <StyledTableRow>
                                            <StyledSubTableCell colSpan={15} style={{ textAlign: 'center' }}>
                                                No budget data found
                                            </StyledSubTableCell>
                                        </StyledTableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );
}

export default NominalBudget;
